import { useMemo, useState, type ReactNode } from "react";
import { CalendarDays, Copy, Plus, X } from "lucide-react";

import { Button } from "@/design-system/primitives";
import { useBootstrap } from "@/lib/api/hooks";
import {
  useAddTimeOff,
  useClinicalTeam,
  useClinicianAvailability,
  useRemoveTimeOff,
  useSaveClinicianAvailability,
} from "@/lib/api/clinicianHooks";
import type { AvailabilityDay, ClinicianAvailability } from "@/types/clinician";
import {
  AVAILABILITY_TIMEZONES,
  DAYS,
  END_OF_DAY,
  MAX_RANGES_PER_DAY,
  QUARTER_HOURS,
  availabilityErrorMessage,
  copyMondayToWeekdays,
  formatInZone,
  formatTime,
  minutesOf,
  validateWeek,
  wallTimeToIso,
  weekFromRanges,
  weeklyPayload,
  type WeeklyHours,
} from "./availability";

const inputClass =
  "w-full rounded border border-line bg-page px-10 py-8 type-body-sm focus-visible:outline-none focus-visible:shadow-focus";
const timeSelectClass =
  "rounded border border-line bg-page px-8 py-6 type-body-sm focus-visible:outline-none focus-visible:shadow-focus";

type ApiErrorLike = {
  status?: number;
  code?: string;
  message?: string;
  payload?: Record<string, unknown>;
} | null;

/** Sync-visit hours a clinician offers, saved to their Healthie calendar. */
export function AvailabilityScreen() {
  const { data: bootstrap } = useBootstrap();
  const supervisor =
    bootstrap?.admin_role === "admin" ||
    bootstrap?.admin_role === "clinical_supervisor";
  const team = useClinicalTeam(supervisor);
  // null = the signed-in clinician's own calendar.
  const [clinicianId, setClinicianId] = useState<string | null>(null);
  const availability = useClinicianAvailability(clinicianId);
  // Lives here, not in the editor: a failed save reloads the calendar, which
  // remounts the editor, and the message must survive that.
  const save = useSaveClinicianAvailability();
  const error = availability.error as ApiErrorLike;

  const managed = useMemo(
    () =>
      (team.data?.clinicians ?? [])
        .filter((member) => member.active && member.healthie_provider_id)
        .sort((a, b) => (a.display_name ?? "").localeCompare(b.display_name ?? "")),
    [team.data],
  );

  return (
    <div className="max-w-[72rem] mx-auto px-20 md:px-35 py-25 md:py-35 flex flex-col gap-20">
      <header className="flex flex-wrap items-end justify-between gap-15">
        <div>
          <span className="type-label-sm text-ink-muted">SCHEDULING</span>
          <h1 className="type-heading-xl mt-4">Availability</h1>
          <p className="type-body-sm text-ink-muted mt-5 max-w-[44rem]">
            When patients can book a live visit with you. Changes are saved
            straight to the Healthie calendar booking reads from.
          </p>
        </div>
        {supervisor && (
          <label className="type-label-sm min-w-[16rem]">
            Clinician
            <select
              className={`${inputClass} mt-5`}
              value={clinicianId ?? ""}
              onChange={(event) => {
                save.reset();
                setClinicianId(event.target.value || null);
              }}
            >
              <option value="">My calendar</option>
              {managed.map((member) => (
                <option key={member.clinician_id} value={member.clinician_id}>
                  {member.display_name || member.email || member.clinician_id}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>

      {availability.isLoading ? (
        <Notice>Loading the Healthie calendar…</Notice>
      ) : error ? (
        <Notice tone="alert">
          {error.code === "availability_editor_disabled"
            ? "Editing availability here isn't switched on yet."
            : error.code === "availability_unavailable"
              ? error.message ||
                "There's no Healthie calendar to edit for this account."
              : availabilityErrorMessage(error, "The Healthie calendar couldn't be loaded.")}
          {supervisor && error.code === "availability_unavailable" && (
            <span className="block mt-4 text-ink-muted">
              Choose a clinician above to manage their hours.
            </span>
          )}
          {error.code !== "availability_editor_disabled" &&
            error.code !== "availability_unavailable" && (
              <button
                type="button"
                className="underline ml-6"
                onClick={() => void availability.refetch()}
              >
                Retry
              </button>
            )}
        </Notice>
      ) : availability.data ? (
        <>
          <WeeklyHoursEditor
            // A failed save remounts the editor on the calendar Healthie holds,
            // so unsaved hours never sit under the "reloaded" message.
            key={`${editorKey(availability.data)}:${save.isError ? save.submittedAt : ""}`}
            availability={availability.data}
            clinicianId={clinicianId}
            save={save}
          />
          <TimeOffSection
            // Switching clinicians must clear a half-entered time off, or it
            // would be submitted for the newly selected calendar.
            key={availability.data.clinician_id}
            availability={availability.data}
            clinicianId={clinicianId}
          />
        </>
      ) : null}
    </div>
  );
}

function editorKey(data: ClinicianAvailability): string {
  return `${data.clinician_id}:${data.timezone}:${JSON.stringify(
    weeklyPayload(weekFromRanges(data.weekly)),
  )}`;
}

function WeeklyHoursEditor({
  availability,
  clinicianId,
  save,
}: {
  availability: ClinicianAvailability;
  clinicianId: string | null;
  save: ReturnType<typeof useSaveClinicianAvailability>;
}) {
  const initialWeek = useMemo(() => weekFromRanges(availability.weekly), [availability.weekly]);
  const [week, setWeek] = useState<WeeklyHours>(initialWeek);
  const [timezone, setTimezone] = useState(availability.timezone);
  const problems = validateWeek(week);
  const invalid = Object.keys(problems).length > 0;
  const dirty =
    timezone !== availability.timezone ||
    JSON.stringify(weeklyPayload(week)) !== JSON.stringify(weeklyPayload(initialWeek));
  const editable = availability.editable;
  const saveError = save.error as ApiErrorLike;
  const timezones = AVAILABILITY_TIMEZONES.some((zone) => zone.value === timezone)
    ? AVAILABILITY_TIMEZONES
    : [{ value: timezone, label: timezone }, ...AVAILABILITY_TIMEZONES];

  const update = (day: AvailabilityDay, index: number, field: "start" | "end", value: string) =>
    setWeek((current) => ({
      ...current,
      [day]: current[day].map((range, at) => (at === index ? { ...range, [field]: value } : range)),
    }));
  const addRange = (day: AvailabilityDay) =>
    setWeek((current) => {
      const last = current[day][current[day].length - 1];
      const lastEnd = last ? minutesOf(last.end) ?? 0 : null;
      const start = lastEnd === null ? 9 * 60 : Math.min(lastEnd + 60, 22 * 60);
      const end = lastEnd === null ? 17 * 60 : Math.min(start + 60, 23 * 60 + 45);
      return { ...current, [day]: [...current[day], { start: hhmm(start), end: hhmm(end) }] };
    });
  const removeRange = (day: AvailabilityDay, index: number) =>
    setWeek((current) => ({ ...current, [day]: current[day].filter((_, at) => at !== index) }));

  return (
    <section className="border border-line-subtle bg-raised" aria-labelledby="weekly-hours">
      <div className="px-20 py-15 border-b border-line-subtle flex flex-wrap items-center justify-between gap-12">
        <div>
          <h2 id="weekly-hours" className="type-heading-sm">
            Weekly hours
          </h2>
          {availability.provider_name && (
            <p className="type-body-sm text-ink-muted mt-2">{availability.provider_name}</p>
          )}
          {availability.appointment_types.length > 0 && (
            <p className="type-label-xs text-ink-muted mt-2">
              For {availability.appointment_types.map((type) => type.name).join(", ")}
            </p>
          )}
        </div>
        <label className="type-label-sm min-w-[14rem]">
          Time zone
          <select
            className={`${inputClass} mt-5`}
            value={timezone}
            disabled={!editable}
            onChange={(event) => setTimezone(event.target.value)}
          >
            {timezones.map((zone) => (
              <option key={zone.value} value={zone.value}>
                {zone.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <ul className="divide-y divide-line-subtle">
        {DAYS.map((day) => {
          const ranges = week[day.value];
          return (
            <li
              key={day.value}
              className="px-20 py-12 grid gap-8 md:grid-cols-[9rem_minmax(0,1fr)] md:items-start"
            >
              <span className="type-label-md pt-6">{day.label}</span>
              <div className="flex flex-col gap-8">
                {ranges.length === 0 && (
                  <span className="type-body-sm text-ink-muted pt-6">Unavailable</span>
                )}
                {ranges.map((range, index) =>
                  editable ? (
                    <div key={index} className="flex flex-wrap items-center gap-8">
                      <select
                        aria-label={`${day.label} start ${index + 1}`}
                        className={timeSelectClass}
                        value={range.start}
                        onChange={(event) => update(day.value, index, "start", event.target.value)}
                      >
                        {timeOptions(range.start)}
                      </select>
                      <span className="text-ink-muted" aria-hidden>
                        –
                      </span>
                      <select
                        aria-label={`${day.label} end ${index + 1}`}
                        className={timeSelectClass}
                        value={range.end}
                        onChange={(event) => update(day.value, index, "end", event.target.value)}
                      >
                        {timeOptions(range.end, true)}
                      </select>
                      <button
                        type="button"
                        className="p-6 rounded hover:bg-sunken focus-visible:outline-none focus-visible:shadow-focus"
                        aria-label={`Remove ${formatTime(range.start)}–${formatTime(range.end)} on ${day.label}`}
                        onClick={() => removeRange(day.value, index)}
                      >
                        <X size={14} aria-hidden />
                      </button>
                    </div>
                  ) : (
                    <span key={index} className="type-body-sm pt-6">
                      {formatTime(range.start)}–{formatTime(range.end)}
                    </span>
                  ),
                )}
                {editable && ranges.length < MAX_RANGES_PER_DAY && (
                  <button
                    type="button"
                    className="self-start inline-flex items-center gap-5 type-label-xs text-ink-muted hover:text-ink"
                    onClick={() => addRange(day.value)}
                  >
                    <Plus size={13} aria-hidden /> Add hours
                  </button>
                )}
                {problems[day.value] && (
                  <span role="alert" className="type-label-xs text-danger">
                    {problems[day.value]}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {editable ? (
        <div className="px-20 py-15 border-t border-line-subtle flex flex-wrap items-center gap-12">
          <Button type="button" variant="secondary" size="sm" onClick={() => setWeek(copyMondayToWeekdays)}>
            <Copy size={13} aria-hidden /> Copy Monday to weekdays
          </Button>
          <div className="ml-auto flex flex-wrap items-center gap-12">
            {save.isSuccess && !dirty && (
              <span role="status" className="type-label-sm text-ink-muted">
                Saved to Healthie.
              </span>
            )}
            {save.isError && (
              <span role="alert" className="type-label-sm text-danger">
                {availabilityErrorMessage(saveError, "Your hours couldn't be saved.")}
              </span>
            )}
            <Button
              type="button"
              variant="primary"
              size="sm"
              loading={save.isPending}
              disabled={!dirty || invalid}
              onClick={() => save.mutate({ clinicianId, timezone, weekly: weeklyPayload(week) })}
            >
              Save hours
            </Button>
          </div>
        </div>
      ) : (
        <p className="px-20 py-12 border-t border-line-subtle type-body-sm text-ink-muted">
          These hours are read-only here.
        </p>
      )}
    </section>
  );
}

function TimeOffSection({
  availability,
  clinicianId,
}: {
  availability: ClinicianAvailability;
  clinicianId: string | null;
}) {
  const add = useAddTimeOff();
  const remove = useRemoveTimeOff();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const timezone = availability.timezone;
  const now = Date.now();
  const upcoming = [...availability.time_off]
    .filter((entry) => new Date(entry.end).getTime() > now)
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  const startIso = start ? wallTimeToIso(start, timezone) : null;
  const endIso = end ? wallTimeToIso(end, timezone) : null;
  const rangeInvalid = Boolean(startIso && endIso && endIso <= startIso);
  // A wall-clock time skipped by a daylight-saving change has no real instant.
  const skippedTime = Boolean((start && !startIso) || (end && !endIso));
  const addError = add.error as ApiErrorLike;
  const removeError = remove.error as ApiErrorLike;

  return (
    <section className="border border-line-subtle bg-raised" aria-labelledby="time-off">
      <div className="px-20 py-15 border-b border-line-subtle">
        <h2 id="time-off" className="type-heading-sm">
          Time off
        </h2>
        <p className="type-body-sm text-ink-muted mt-2">
          Blocks booking on your calendar. Times are in your calendar's time zone.
        </p>
      </div>
      {upcoming.length === 0 ? (
        <p className="px-20 py-12 type-body-sm text-ink-muted">No upcoming time off.</p>
      ) : (
        <ul className="divide-y divide-line-subtle">
          {upcoming.map((entry) => (
            <li key={entry.id} className="px-20 py-10 flex flex-wrap items-center gap-10">
              <CalendarDays size={14} className="text-ink-muted" aria-hidden />
              <span className="type-body-sm">
                {formatInZone(entry.start, timezone)} – {formatInZone(entry.end, timezone)}
              </span>
              {entry.note && <span className="type-body-sm text-ink-muted">{entry.note}</span>}
              {!entry.removable ? (
                <span className="ml-auto type-label-xs text-ink-muted">Set in Healthie</span>
              ) : (
                availability.editable && (
                  <button
                    type="button"
                    className="ml-auto type-label-xs text-ink-muted hover:text-danger"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate({ clinicianId, id: entry.id })}
                  >
                    Remove
                  </button>
                )
              )}
            </li>
          ))}
        </ul>
      )}
      {availability.editable && (
        <form
          className="px-20 py-15 border-t border-line-subtle grid gap-12 md:grid-cols-[1fr_1fr_1.4fr_auto] md:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            if (!startIso || !endIso || rangeInvalid) return;
            add.mutate(
              { clinicianId, start: startIso, end: endIso, note: note.trim() || null },
              {
                onSuccess: () => {
                  setStart("");
                  setEnd("");
                  setNote("");
                },
              },
            );
          }}
        >
          <label className="type-label-sm">
            Starts
            <input
              type="datetime-local"
              step={900}
              className={`${inputClass} mt-5`}
              value={start}
              onChange={(event) => setStart(event.target.value)}
            />
          </label>
          <label className="type-label-sm">
            Ends
            <input
              type="datetime-local"
              step={900}
              className={`${inputClass} mt-5`}
              value={end}
              onChange={(event) => setEnd(event.target.value)}
            />
          </label>
          <label className="type-label-sm">
            Note (optional)
            <input
              className={`${inputClass} mt-5`}
              value={note}
              maxLength={200}
              onChange={(event) => setNote(event.target.value)}
              placeholder="e.g. Conference"
            />
          </label>
          <Button
            type="submit"
            variant="secondary"
            size="sm"
            loading={add.isPending}
            disabled={!startIso || !endIso || rangeInvalid}
          >
            Add time off
          </Button>
          {(skippedTime || rangeInvalid || add.isError || remove.isError) && (
            <span role="alert" className="md:col-span-4 type-label-xs text-danger">
              {skippedTime
                ? "That time doesn't exist on that date because the clocks change. Pick another time."
                : rangeInvalid
                ? "Time off must end after it starts."
                : add.isError
                  ? availabilityErrorMessage(addError, "Time off couldn't be added.")
                  : availabilityErrorMessage(removeError, "Time off couldn't be removed.")}
            </span>
          )}
        </form>
      )}
    </section>
  );
}

/** Quarter hours; an end time can also be 23:59, which runs to midnight. */
function timeOptions(current: string, end = false) {
  const base = end ? [...QUARTER_HOURS, END_OF_DAY] : QUARTER_HOURS;
  const values = base.includes(current) ? base : [current, ...base];
  return values.map((value) => (
    <option key={value} value={value}>
      {formatTime(value)}
    </option>
  ));
}

function hhmm(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

function Notice({ children, tone }: { children: ReactNode; tone?: "alert" }) {
  return (
    <div
      role={tone === "alert" ? "alert" : "status"}
      className="border border-line-subtle bg-raised px-20 py-20 type-body-sm"
    >
      {children}
    </div>
  );
}
